import { useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { FeatureFlagsMap } from 'inquiry/lib/interfaces/feature-flags';
import { TrackingEventsClient } from 'lib/clients/TrackingEventsClient';
import { Inquiry } from 'verify/models/inquiry';
import { RootState } from 'verify/redux/store';

/**
 * @name `useTrackVideoDimensions`
 *
 * A hook that keeps track of video dimensions and logs them if they change
 *
 */
const useTrackVideoDimensions = () => {
  const { pathname: pathName } = useLocation();

  const inquiry = useSelector<RootState, Inquiry>((state) => state.inquiry.inquiry);
  const featureFlags = useSelector<RootState, FeatureFlagsMap>(
    (state) => state.inquiry.featureFlags,
  );
  const step = inquiry?.attributes?.['next-step'];

  // NB: use a ref because this is typically used within a timeout
  const dimensions = useRef<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });

  return {
    videoDimensionsUpdated: (width: number | undefined, height: number | undefined) => {
      if (
        width != null &&
        height != null &&
        (dimensions.current.width !== width || dimensions.current.height !== height)
      ) {
        TrackingEventsClient.track(
          {
            type: 'inquiry.camera.dimensions',
            data: {
              old_width: dimensions.current.width,
              old_height: dimensions.current.height,
              new_width: width,
              new_height: height,
            },
            stepName: step?.name,
            pathName,
          },
          featureFlags,
        );
        dimensions.current = { width, height };
      }
    },
  };
};

export default useTrackVideoDimensions;
